<template>
  <div class="chart chart--new">
    <div v-if="canRenderLineChart">
      <div class="chart-content__header">
        <ChartFiltersV2
          :filters="chartFilters"
          :is-loading="loading || isChartAnimationInProgress"
          @on-select-filter="onSelectFilter"
        />
      </div>
      <ChartNoData
        v-if="isChartEmpty"
        height="320px"
        text="Não há informações de rentabilidade dos seus ativos referente ao período selecionado"
      />
      <LineChart
        v-else
        class="line-chart-v2__wrapper"
        :series="seriesData"
        :benchmark="benchmarkData"
        :height="heightChart"
        :labels="labels"
        :show-axis-x-labels="false"
        :visible-tooltips-index="[0, 1]"
        @on-animation-state-change="handleOnAnimationStateChange"
      />
      <ChartFooter
        v-if="!isChartEmpty"
        :start-date="startDate"
        :end-date="endDate"
        :date-format="labelDateFormat"
      />
    </div>
    <template
      v-else-if="loading || dashStatusRequestStatus === RequestStatus.Loading"
    >
      <MainLoading is-right-loading />
    </template>
    <template
      v-else-if="isError || dashStatusRequestStatus === RequestStatus.Error"
    >
      <MainError
        :title="errorTitle"
        description="Aguarde alguns minutos e tente novamente."
        :with-filter="canIgnoreDashStatusRequest"
        @on-retry="handleOnRetry"
      />
    </template>
    <div :class="['transaction_container', 'transaction_container--new']">
      <template
        v-if="
          balancesBrokerRequestStatus === RequestStatus.Loading ||
          dashStatusRequestStatus === RequestStatus.Loading
        "
      >
        <SkeletonLoader
          show-animation
          class="chart__buttons--new"
          data-testid="chart-buttons"
        >
          <div />
        </SkeletonLoader>
      </template>
      <template v-else>
        <div
          v-for="(button, index) in buttons"
          :key="index"
          class="transaction_container__item"
        >
          <div>
            <ButtonBoxLaunch
              class="transaction_container__button"
              :type="button.type"
              :label="button.label"
              :icon="button.icon"
              :data-testid="`button-${button.action}`"
              @click="buttonAction(button)"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';

import { RouteMixin } from '@/modules/common/mixins/route';
import { SkeletonLoader, ButtonBoxLaunch } from '@warrenbrasil/nebraska-web';
import { ILegend } from '@/modules/common/components/charts/LegendChart';
import {
  IDashButtons,
  getChart,
  IHeroBalances,
  HomeChartDateFilters,
  HomeChartBenchmarkFilters,
  HomeChartData,
  HomeChartAxisY,
  HomeChartAxisX,
  HomeChartAxisYKeys
} from '@/modules/trade/services/account';
import { moneyViewer as money } from '@/modules/common/helpers/currency';
import { MainError } from '@/modules/trade/components/MainError';
import { MainLoading } from '@/modules/trade/components/MainLoading';

import {
  ChartNoData,
  ChartFilters,
  LineChart,
  ChartFooter
} from '@/modules/common/components/charts/v2';
import {
  ChartFilters as ChartFiltersV2,
  ChartFiltersTypes,
  ChartFilterValue,
  ChartFiltersKeys
} from './components/ChartFilters';
import { getAxisXLabelDateFormatByFilter } from './helpers';

import {
  trackChartFilterClick,
  trackSearchButtonClick,
  trackTradeWithdrawClick,
  trackChartRequestError,
  trackChartRetryClick
} from '@/modules/trade/views/TradeView/trackers';

import { DashStatusButtonActions } from '@/modules/trade/types/dash-status';

import { ChartSeries } from './types';
import { filtersPortfolioApiIdDependentButtons } from '@/modules/trade/views/TradeView/helpers';
import { RequestStatus } from '@/modules/trade/store/trade';
import { CustomOperationSummary } from '@/modules/trade/services/custom-operations';
import { logError } from '@/modules/trade/helpers/logs';

const tradePositionsModule = namespace('tradeModule');

@Component({
  filters: {
    money
  },
  components: {
    ChartFilters,
    LineChart,
    MainError,
    SkeletonLoader,
    ChartFooter,
    MainLoading,
    ButtonBoxLaunch,
    ChartFiltersV2,
    ChartNoData
  }
})
export default class Chart extends mixins(RouteMixin) {
  @Prop({ type: Object, required: false })
  readonly balances?: IHeroBalances;

  @Prop({ type: Array, required: false, default: () => [] })
  readonly accountButtons?: IDashButtons[];

  @Prop({ type: Boolean, default: false })
  readonly isCustomerBlocked!: boolean;

  @tradePositionsModule.State('balancesBrokerRequestStatus')
  readonly balancesBrokerRequestStatus!: RequestStatus;

  @tradePositionsModule.State('dashStatusRequestStatus')
  readonly dashStatusRequestStatus!: RequestStatus;

  @tradePositionsModule.Action('fetchDashStatus')
  readonly fetchDashStatus!: Function;

  @tradePositionsModule.State('customOperations')
  readonly customOperations!: CustomOperationSummary[];

  readonly RequestStatus = RequestStatus;
  readonly DashStatusButtonActions = DashStatusButtonActions;
  readonly ChartFiltersKeys = ChartFiltersKeys;
  loading = true;
  isError = false;
  series: ChartSeries[] = [];
  labels: string[] = [];
  filters: ILegend[] = [];
  labelDateFormat = '';
  private seriesValues!: ChartSeries[];
  private lastTotalBalance!: number;
  private isChartAnimationInProgress = false;
  private chartData: HomeChartData | null = null;

  chartFilters: ChartFiltersTypes = {
    [ChartFiltersKeys.DATE_FILTER]: {
      value: null,
      options: []
    },
    [ChartFiltersKeys.BENCHMARK_FILTER]: {
      value: null,
      options: []
    }
  };

  created() {
    this.fetchesChartData();
  }

  handleOnAnimationStateChange(isAnimationInProgress: boolean) {
    this.isChartAnimationInProgress = isAnimationInProgress;
  }

  get heightChart() {
    const height = window.innerHeight;
    if (height >= 1000) return '400px';
    else if (height >= 900) return '380px';
    else if (height >= 721) return '280px';
    else if (height > 650) return '190px';
    else return '150px';
  }

  get hasCustomOperations() {
    return this.customOperations?.length > 0;
  }

  get isChartEmpty() {
    return (
      !this.seriesData ||
      !this.seriesData.data ||
      this.seriesData.data.length === 0
    );
  }

  get buttons() {
    const accountButtons = this.accountButtons || [];

    // Se o serviço de buscar o saldo teve erro, a gente não tem o portfolioApiId
    return this.balancesBrokerRequestStatus === RequestStatus.Error
      ? filtersPortfolioApiIdDependentButtons(accountButtons)
      : accountButtons;
  }

  public buttonAction(button: IDashButtons) {
    switch (button.action) {
      case DashStatusButtonActions.Search:
        trackSearchButtonClick();
        this.$router.push({ name: 'tradeSearch' });
        break;
      case DashStatusButtonActions.Withdrawal:
        trackTradeWithdrawClick();
        break;
      case DashStatusButtonActions.BrokerageNotes:
        this.$router.push({ name: 'tradingNotes', query: { module: 'trade' } });
        break;
      default:
        break;
    }
  }

  get startDate() {
    return this.labels[0];
  }

  get endDate() {
    return this.labels[this.labels.length - 1];
  }

  get seriesData() {
    const [series] = this.series;

    return {
      ...series,
      comparator: []
    };
  }

  get benchmarkData() {
    return this.series[1];
  }

  get hasBalance() {
    const hasGrossBalance =
      this.balances?.totalBalance?.value &&
      this.balances.totalBalance.value > 0;

    return hasGrossBalance;
  }

  get canIgnoreDashStatusRequest() {
    return !!(
      this.dashStatusRequestStatus === RequestStatus.Default ||
      (this.dashStatusRequestStatus === RequestStatus.Error && this.hasBalance)
    );
  }

  get errorTitle() {
    return this.canIgnoreDashStatusRequest
      ? 'Não foi possível carregar o gráfico'
      : 'Não foi possível carregar os dados da sua conta';
  }

  get canRenderLineChart() {
    return !this.isError && this.canIgnoreDashStatusRequest && this.chartData;
  }

  async handleOnRetry() {
    trackChartRetryClick();

    this.canIgnoreDashStatusRequest
      ? await this.fetchesChartData()
      : await this.fetchDashStatus();
  }

  private buildSeriesWithFinOneData(axisY: HomeChartAxisY[]) {
    const series = [];

    const mainSeries = axisY.find(
      data => data.key === HomeChartAxisYKeys.Portfolio
    );

    if (mainSeries)
      series.push({
        name: mainSeries.description,
        data: mainSeries.values.map(value => (value ? value.value : null)),
        benchmarkComparison: mainSeries.values.map(value =>
          value ? value.benchmarkComparison : {}
        )
      });

    if (this.chartFilters[ChartFiltersKeys.BENCHMARK_FILTER].value) {
      const benchmarkSeries = axisY.find(
        data =>
          data.key ===
          this.chartFilters[ChartFiltersKeys.BENCHMARK_FILTER].value
      );

      if (benchmarkSeries)
        series.push({
          name: benchmarkSeries.description,
          data: benchmarkSeries.values.map(value =>
            value ? value.value : null
          )
        });
    }

    return series;
  }

  private buildSeries(axisY: HomeChartAxisY[]): ChartSeries[] {
    const series = [];

    const portfolio = axisY.find(
      value => value.key === HomeChartAxisYKeys.Portfolio
    );
    if (portfolio) {
      series.push({
        name: 'Meus ativos',
        data: portfolio.values.map(value =>
          value ? value.value : null
        ) as number[],
        benchmarkComparison: portfolio.values.map(value =>
          value ? value.benchmarkComparison : {}
        )
      });
    }

    const benchmark = axisY.find(
      value => value.key === HomeChartAxisYKeys.Ibovespa
    );
    if (benchmark) {
      series.push({
        name: 'Ibovespa',
        data: benchmark?.values.map(value =>
          value ? value.value : null
        ) as number[]
      });
    }

    return series;
  }

  async fetchesChartData() {
    try {
      this.isError = false;
      this.loading = true;
      this.isChartAnimationInProgress = true;

      await this.fetchChartBff();
    } catch (error) {
      logError(error, 'request_chart_error');
      trackChartRequestError();
      this.isError = true;
      this.series = [];
    } finally {
      this.loading = false;
    }
  }

  private async fetchChartBff() {
    const { data } = await getChart(
      this.chartFilters[ChartFiltersKeys.DATE_FILTER].value,
      this.chartFilters[ChartFiltersKeys.BENCHMARK_FILTER].value
    );
    this.chartData = data;
    this.buildChartData();

    if (this.isChartEmpty) {
      this.handleOnAnimationStateChange(false);
    }
  }

  public buildValues(axisY: HomeChartAxisY[], valueType: string) {
    const typeValues = axisY.filter(value => value.key === valueType);
    const data = typeValues[0]?.values.map(value =>
      value ? value.value : null
    );

    if (!data) return [];

    return [
      {
        name: typeValues[0].description,
        data
      }
    ];
  }

  public buildLabels(axisX: HomeChartAxisX[]): string[] {
    return axisX[0].values;
  }

  private buildChartData() {
    this.buildChartFilters(this.chartData!);
    this.lastTotalBalance = this.chartData!.lastTotalBalance?.value;
    this.series = this.buildSeriesWithFinOneData(this.chartData!.axisY);
    this.labels = this.buildLabels(this.chartData!.axisX);
    this.seriesValues = this.buildValues(
      this.chartData!.axisY,
      HomeChartAxisYKeys.TotalBalance
    );
  }

  private buildChartFilters(chartData: HomeChartData) {
    const defaultDateFilter = this.getDefaultFilterValue(
      chartData?.defaultFilter,
      chartData.filters
    );
    this.changeChartFilterOptions(
      ChartFiltersKeys.DATE_FILTER,
      chartData.filters
    );
    if (!this.chartFilters[ChartFiltersKeys.DATE_FILTER].value) {
      this.changeChartFilterValue(
        ChartFiltersKeys.DATE_FILTER,
        defaultDateFilter
      );
    }

    const defaultBenchmarkFilter = this.getDefaultFilterValue(
      chartData?.defaultBenchmarkFilter,
      chartData.benchmarkFilters || []
    );
    this.changeChartFilterOptions(
      ChartFiltersKeys.BENCHMARK_FILTER,
      chartData.benchmarkFilters || []
    );
    if (!this.chartFilters[ChartFiltersKeys.BENCHMARK_FILTER].value) {
      this.changeChartFilterValue(
        ChartFiltersKeys.BENCHMARK_FILTER,
        defaultBenchmarkFilter
      );
    }

    this.labelDateFormat = getAxisXLabelDateFormatByFilter(
      this.chartFilters[ChartFiltersKeys.DATE_FILTER].value
    );
  }

  private getDefaultFilterValue(
    defaultFilterIndex: number | null,
    options: HomeChartDateFilters[] | HomeChartBenchmarkFilters[]
  ) {
    return defaultFilterIndex || defaultFilterIndex === 0
      ? options[defaultFilterIndex]?.key
      : null;
  }

  private buildChartFiltersOptions(
    options: HomeChartDateFilters[] | HomeChartBenchmarkFilters[]
  ) {
    return options.map(option => ({
      value: option.key,
      label: option.description
    }));
  }

  private changeChartFilterOptions(
    filterKey: ChartFiltersKeys,
    options: HomeChartDateFilters[] | HomeChartBenchmarkFilters[]
  ) {
    this.chartFilters[filterKey].options =
      this.buildChartFiltersOptions(options);
  }

  private changeChartFilterValue(
    filterKey: ChartFiltersKeys,
    value: ChartFilterValue
  ) {
    this.chartFilters[filterKey].value = value;
  }

  async onSelectFilter(filterKey: ChartFiltersKeys, value: ChartFilterValue) {
    this.changeChartFilterValue(filterKey, value);
    this.trackFilterClick(filterKey);
    await this.fetchesChartData();
  }

  private trackFilterClick(filterKey: ChartFiltersKeys) {
    const filterClicked = this.chartFilters[filterKey].options.find(
      filter => filter.value === this.chartFilters[filterKey].value
    );
    const filter = filterClicked?.label || '';

    trackChartFilterClick(filter);
  }
}
</script>

<style lang="less" scoped>
:deep(.label--secondary) {
  .text-p-4();
}

:deep(.button-box-decide) {
  border-radius: @size-radius-x300;
}

:deep(.label) {
  white-space: nowrap;
}

.line-chart__wrapper {
  height: 300px;

  @media only screen and (max-height: 512px) {
    height: 120px;
  }

  @media only screen and (min-height: 513px) and (max-height: 640px) {
    height: 150px;
  }

  @media only screen and (min-height: 641px) and (max-height: 690px) {
    height: 200px;
  }

  @media only screen and (min-height: 691px) and (max-height: 740px) {
    height: 250px;
  }

  @media only screen and (min-height: 741px) and (max-height: 790px) {
    height: 300px;
  }

  @media only screen and (min-height: 791px) and (max-height: 840px) {
    height: 350px;
  }

  @media only screen and (min-height: 841px) {
    height: 450px;
  }

  @media only screen and (max-width: 480px) {
    max-height: 300px;
  }
}

.chart {
  display: flex;
  flex-direction: column;

  &__buttons {
    &--new div {
      height: 132px;
      width: 140px;
      border-radius: @size-radius-x300;
    }

    &--old div {
      height: 48px;
      width: 140px;
      border-radius: @size-radius-x200;
    }
  }

  &--new {
    .template-loader__container__main-chart {
      :deep(.main-error__container) {
        height: 300px;

        @media only screen and (max-height: 512px) {
          height: 120px;
        }

        @media only screen and (min-height: 513px) and (max-height: 640px) {
          height: 150px;
        }

        @media only screen and (min-height: 641px) and (max-height: 690px) {
          height: 200px;
        }

        @media only screen and (min-height: 691px) and (max-height: 740px) {
          height: 250px;
        }

        @media only screen and (min-height: 741px) and (max-height: 790px) {
          height: 300px;
        }

        @media only screen and (min-height: 791px) and (max-height: 840px) {
          height: 350px;
        }

        @media only screen and (min-height: 841px) {
          height: 450px;
        }

        @media only screen and (max-width: 480px) {
          max-height: 300px;
        }
      }
    }

    .transaction_container {
      @media only screen and (min-height: 721px) and (max-height: 899px) {
        padding-top: @size-spacing-x500;
      }

      @media only screen and (max-height: 720px) {
        padding-top: @size-spacing-x400;
      }
    }

    .line-chart-v2__filters,
    .template-loader__container__filters {
      @media only screen and (max-height: 650px) {
        margin-top: @size-spacing-x350;
      }

      @media only screen and (min-height: 651px) and (max-height: 720px) {
        margin-top: @size-spacing-x500;
      }

      @media only screen and (min-height: 721px) and (max-height: 899px) {
        margin-top: @size-spacing-x600;
      }
    }
  }
}

.chart-content {
  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__subtitle {
    display: flex;
    flex-direction: row;
    font-size: 20px;
    color: @text-primary;
    align-items: center;

    div:first-of-type,
    div:last-of-type {
      display: flex;
      flex-direction: row;
    }
  }

  &__subtitle-line {
    margin-right: @size-spacing-x300 !important;
    border: @size-border-x400 solid @theme-primary;
    width: @size-spacing-x350;
    height: 1px;
  }

  &__subtitle-secondary {
    width: 80px;
    color: @text-secondary;
    letter-spacing: -1px;
    margin-left: @size-spacing-x300;
  }
}

.transaction_container {
  display: flex;
  align-items: center;
  padding-top: @size-spacing-x800;
  overflow: auto;

  &--new {
    flex-wrap: no-wrap;
  }

  &--old {
    flex-wrap: wrap;
  }

  &__item {
    margin-right: @size-spacing-x300;
    margin-bottom: @size-spacing-x300;

    &:last-of-type {
      margin-right: 0;
    }

    &:nth-of-type(1) {
      margin-left: @size-spacing-x150;
    }
  }

  &__button {
    :deep(.label) {
      white-space: initial;
    }
  }
}

.transaction_unrealized_balance {
  margin-left: 24px;
}

.template-loader__container__filters__loading {
  display: flex;
  justify-content: space-between;
  margin: @size-spacing-x800 0 @size-spacing-x300 0;
  height: 40px;

  &__right {
    width: 100px;
    background-color: @background-tertiary;
    border-radius: @size-spacing-x600;
  }

  &__left {
    width: 170px;
    background-color: @background-tertiary;
    border-radius: @size-spacing-x600;
  }
}
</style>
